import React, { useState, useEffect, useMemo } from 'react'
import { api, formatCurrency } from '../../utils/api'
import '../../styles/pages.css'

const SORTS = {
  margin: (a, b) => b.margin - a.margin,
  comp: (a, b) => a.comp - b.comp,
  bp: (a, b) => a.bp - b.bp,
  sp: (a, b) => a.sp - b.sp,
  mp: (a, b) => b.mp - a.mp,
  priced: (a, b) => (a.priced < b.priced ? 1 : -1),
}

function AudioRaider() {
  const [rows, setRows] = useState([])
  const [meta, setMeta] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [tier, setTier] = useState('all')
  const [src, setSrc] = useState('all')
  const [q, setQ] = useState('')
  const [pickupOnly, setPickupOnly] = useState(false)
  const [sort, setSort] = useState('margin')

  useEffect(() => { load() }, [])
  async function load() {
    setLoading(true); setError(null)
    try {
      const res = await api.get('/api/deals/audio-raider')
      setRows(res.rows || [])
      setMeta({ generatedAt: res.generatedAt, windowDays: res.windowDays })
    } catch (e) {
      console.error('Audio Raider load failed:', e); setError(e.message || 'Failed to load')
    } finally { setLoading(false) }
  }

  const filtered = useMemo(() => {
    const r = rows.filter(d =>
      (tier === 'all' || d.tier === tier) &&
      (src === 'all' || d.src === src) &&
      (!pickupOnly || d.collectionOnly) &&
      (!q || `${d.name} ${d.cat} ${d.cond}`.toLowerCase().includes(q.toLowerCase())))
    return [...r].sort(SORTS[sort])
  }, [rows, tier, src, q, pickupOnly, sort])

  const tierA = filtered.filter(d => d.tier === 'A')
  const totalA = tierA.reduce((s, d) => s + d.margin, 0)

  if (loading) return <div className="tableWrap" style={{ padding: 24 }}>Loading buy opportunities…</div>
  if (error) return <div className="tableWrap status--error" style={{ padding: 24 }}>{error}</div>

  return (
    <div>
      <div className="statsRow">
        <div className="statBox statBox--blue"><div className="statBox__value">{filtered.length}</div><div className="statBox__label">Opportunities</div></div>
        <div className="statBox statBox--green"><div className="statBox__value">{tierA.length}</div><div className="statBox__label">Tier A · credible</div></div>
        <div className="statBox statBox--amber"><div className="statBox__value">{filtered.length - tierA.length}</div><div className="statBox__label">Tier B · suspect</div></div>
        <div className="statBox statBox--mint"><div className="statBox__value">{formatCurrency(totalA)}</div><div className="statBox__label">Tier-A margin</div></div>
      </div>

      <div className="raiderNote">
        Active comps at/below the buy price we calculated. <strong>Tier B</strong> (comp &lt; 35% of SP or &lt; £100)
        is usually an opening bid, a spares listing, or a mis-scored accessory — read it with suspicion.
        Comps were captured at pricing time, so older rows may have ended.
        {meta && <span className="raiderMeta"> Window {meta.windowDays}d · refreshed {new Date(meta.generatedAt).toLocaleTimeString('en-GB')}</span>}
      </div>

      <div className="raiderControls">
        <input className="raiderSearch" type="search" placeholder="Search item, brand, category…" value={q} onChange={e => setQ(e.target.value)} />
        <div className="statusToggle">
          {['all', 'A', 'B'].map(t => <button key={t} className={tier === t ? 'active' : ''} onClick={() => setTier(t)}>{t === 'all' ? 'All' : `Tier ${t}`}</button>)}
        </div>
        <div className="statusToggle">
          {['all', 'eBay', 'Reverb'].map(s => <button key={s} className={src === s ? 'active' : ''} onClick={() => setSrc(s)}>{s === 'all' ? 'All' : s}</button>)}
        </div>
        <label className="raiderCheck"><input type="checkbox" checked={pickupOnly} onChange={e => setPickupOnly(e.target.checked)} /> Collection-only</label>
      </div>

      <div className="tableWrap">
        <table>
          <thead>
            <tr>
              <th>Item</th><th>Src</th>
              <th className="clickable" onClick={() => setSort('comp')}>Comp £</th>
              <th className="clickable" onClick={() => setSort('bp')}>BP £</th>
              <th className="clickable" onClick={() => setSort('sp')}>SP £</th>
              <th>Below BP</th>
              <th className="clickable" onClick={() => setSort('margin')}>Margin £</th>
              <th className="clickable" onClick={() => setSort('mp')}>Margin %</th>
              <th className="clickable" onClick={() => setSort('priced')}>Priced</th>
              <th>Tier</th><th>Listing</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((d, i) => (
              <tr key={i} className={d.tier === 'B' ? 'raiderTierB' : ''}>
                <td>
                  <div className="productCell__name">{d.name}</div>
                  <div className="productCell__sku">
                    {[d.cat, d.cond, d.geo].filter(Boolean).join(' · ')}
                    {d.isNew ? <span className="chip">new</span> : null}
                    {d.collectionOnly ? <span className="chip">collection</span> : null}
                  </div>
                </td>
                <td>{d.src}</td>
                <td>{formatCurrency(d.comp)}</td>
                <td>{formatCurrency(d.bp)}</td>
                <td>{formatCurrency(d.sp)}</td>
                <td>−{formatCurrency(d.below)}</td>
                <td><strong>{formatCurrency(d.margin)}</strong></td>
                <td>{d.mp}%</td>
                <td>{d.priced.slice(5)}</td>
                <td><span className={`status status--${d.tier === 'A' ? 'active' : 'pending'}`}>{d.tier}</span></td>
                <td><a href={d.url} target="_blank" rel="noopener noreferrer">View →</a></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={11} style={{ padding: 24, textAlign: 'center' }}>No opportunities match these filters.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default AudioRaider
